<template>
  <v-container class="pa-4">
    <v-row align="center">
      <v-col>
        <month-paginator v-model="currentDate" :disabled="isLoading" />
      </v-col>
      <v-col cols="12" sm="auto" class="d-flex ga-2 flex-wrap">
        <v-btn
          variant="outlined"
          color="primary"
          :prepend-icon="mdiFilePdfBox"
          :disabled="isLoading"
          @click="exportMetrics('pdf')"
        >
          Exportar PDF
        </v-btn>
        <v-btn
          variant="outlined"
          color="primary"
          :prepend-icon="mdiFileTable"
          :disabled="isLoading"
          @click="exportMetrics('excel')"
        >
          Exportar Excel
        </v-btn>
      </v-col>
    </v-row>

    <v-row v-if="isLoading">
      <v-col v-for="i in 4" :key="i" cols="6" sm="3">
        <v-skeleton-loader class="rounded-lg" type="image" />
      </v-col>
    </v-row>

    <v-fade-transition>
      <v-row v-if="tenant && !isLoading">
        <v-col v-for="kpi in kpiCards" :key="kpi.label" cols="6" sm="3">
          <v-card :variant="isDark ? 'flat' : undefined" :color="isDark ? 'surface-bright' : undefined" class="rounded-lg">
            <v-card-text class="d-flex align-center ga-3 py-3">
              <v-icon
                :icon="kpi.icon"
                size="32"
                :color="kpi.color"
                class="stat-icon"
              />
              <div>
                <div
                  class="text-h5 font-weight-bold"
                  :style="{ color: kpi.color }"
                >
                  {{ kpi.value }}
                </div>
                <div class="text-caption text-medium-emphasis">
                  {{ kpi.label }}
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-fade-transition>

    <v-fade-transition>
      <v-row v-if="!isLoading && companies.length">
        <v-col cols="12" md="6">
          <v-card :variant="isDark ? 'flat' : undefined" :color="isDark ? 'surface-bright' : undefined" class="rounded-lg">
            <v-card-title class="text-subtitle-1">
              Faturamento por Empresa
            </v-card-title>
            <v-card-text>
              <v-skeleton-loader
                v-if="loadingRevenueByCompany"
                type="image"
                height="300"
              />
              <v-chart
                v-else-if="revenueByCompanyChartOption.series"
                :option="revenueByCompanyChartOption"
                :autoresize="true"
                :style="{ height: chartHeight }"
              />
              <div
                v-else
                class="d-flex align-center justify-center text-medium-emphasis"
                style="height: 300px"
              >
                Sem dados disponíveis
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card :variant="isDark ? 'flat' : undefined" :color="isDark ? 'surface-bright' : undefined" class="rounded-lg">
            <v-card-title class="text-subtitle-1">
              Documentos por Empresa
            </v-card-title>
            <v-card-text>
              <v-chart
                v-if="documentsChartOption.series"
                :option="documentsChartOption"
                :autoresize="true"
                :style="{ height: chartHeight }"
              />
              <div
                v-else
                class="d-flex align-center justify-center text-medium-emphasis"
                style="height: 300px"
              >
                Sem dados disponíveis
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-fade-transition>

    <v-row>
      <v-col cols="12">
        <v-data-table
          class="rounded-lg"
          item-value="company_id"
          :headers="headers"
          :items="filteredCompanies"
          :loading="isLoading"
          :items-per-page="-1"
          :sort-by="[{ key: 'total_document_count', order: 'desc' }]"
        >
          <template v-slot:top>
            <v-text-field
              v-model="searchTerm"
              :prepend-inner-icon="mdiMagnify"
              label="Buscar por Razão Social ou CNPJ"
              hide-details
              clearable
              class="pa-4"
            />
          </template>
          <template v-slot:item.company_cnpj="{ item }">
            {{ formatCnpj(item.company_cnpj) }}
          </template>
          <template v-slot:item.revenue="{ item }">
            {{ item.revenue != null ? formatMoney(item.revenue) : "—" }}
          </template>
          <template v-slot:item.devices_by_type="{ item }">
            <v-chip
              v-for="device in item.devices_by_type"
              :key="device.device_type"
              size="small"
              class="mr-1"
            >
              {{ device.device_type_label }}: {{ device.count }}
            </v-chip>
          </template>
          <template v-slot:item.total_document_count="{ item }">
            {{ formatNumber(item.total_document_count) }}
          </template>
          <template v-slot:no-data>
            <div
              class="d-flex flex-column align-center pa-8 text-medium-emphasis"
            >
              <v-icon :icon="mdiFileSearchOutline" size="64" />
              <div class="text-h6 mb-1">Nenhuma empresa encontrada</div>
              <div class="text-body-2">
                Não há dados disponíveis para {{ monthLabel }}
              </div>
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useTheme } from "vuetify";
import { useRoute, useRouter } from "vue-router";
import {
  mdiFilePdfBox,
  mdiFileTable,
  mdiFileSearchOutline,
  mdiMagnify,
  mdiFileDocumentMultiple,
  mdiStore,
  mdiAccessPoint,
  mdiAccountGroup,
} from "@mdi/js";
import { format, startOfMonth, endOfMonth, parse, getYear } from "date-fns";
import { ptBR } from "date-fns/locale";
import VChart from "vue-echarts";
import "@/plugins/echarts";
import config from "@/config";
import { formatNumberLocal } from "@/filters";
import MonthPaginator from "@/components/MonthPaginator.vue";

const SERIES_COLORS = [
  "#4f7cff",
  "#34d399",
  "#f472b6",
  "#fbbf24",
  "#818cf8",
  "#38bdf8",
  "#fb923c",
  "#a78bfa",
  "#2dd4bf",
  "#f87171",
  "#a3e635",
  "#e879f9",
];
import { formatMoney } from "@/utils";
import { useSnackbar } from "@/plugins/snackbar";
import {
  useBillingMetricsQuery,
  exportConsolidatedBillingMetrics,
} from "@/queries/modules/billing";
import { useRevenueByCompanyQuery } from "@/queries/modules/analytics";

const route = useRoute();
const router = useRouter();
const $snackbar = useSnackbar();
const theme = useTheme();
const isDark = computed(() => theme.global.current.value.dark);
const chartLabel = computed(() =>
  isDark.value ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.55)",
);
const chartGrid = computed(() =>
  isDark.value ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.08)",
);

const headers = [
  { title: "Empresa", key: "company_name" },
  { title: "CNPJ", key: "company_cnpj", sortable: false },
  {
    title: "Faturamento",
    key: "revenue",
    align: "end",
  },
  {
    title: "Dispositivos",
    key: "active_device_count",
    align: "end",
  },
  { title: "Tipos de dispositivo", key: "devices_by_type", sortable: false },
  {
    title: "Documentos",
    key: "total_document_count",
    align: "end",
  },
];

const initialDate = route.query.month
  ? parse(route.query.month, "yyyy-MM", new Date())
  : new Date();
const currentDate = ref(initialDate);

const tenantId = computed(() => {
  return route.params.tenant_id ? Number(route.params.tenant_id) : null;
});

const filters = computed(() => {
  const monthStart = startOfMonth(currentDate.value);
  const monthEnd = endOfMonth(currentDate.value);
  return {
    date_start: format(monthStart, "yyyy-MM-dd"),
    date_end: format(monthEnd, "yyyy-MM-dd"),
    environment: "production",
  };
});

const { data: metricsData, isLoading } = useBillingMetricsQuery(
  tenantId,
  filters,
);

const tenant = computed(() => metricsData.value?.tenant || null);

const searchTerm = ref("");

// Analytics queries scoped to this tenant
const analyticsParams = computed(() => {
  const id = tenantId.value;
  if (!id) return null;
  return {
    ...filters.value,
    tenant_id: id,
  };
});

const { data: revenueByCompany, isLoading: loadingRevenueByCompany } =
  useRevenueByCompanyQuery(analyticsParams);

// Merge billing companies with per-company revenue
const revenueByCompanyMap = computed(() => {
  const map = new Map();
  for (const entry of revenueByCompany.value?.data || []) {
    map.set(entry.company_id, entry);
  }
  return map;
});

const companies = computed(() => {
  const data = metricsData.value?.companies || [];
  const revenueMap = revenueByCompanyMap.value;
  return [...data]
    .map((c) => {
      const rev = revenueMap.get(c.company_id);
      return {
        ...c,
        revenue: rev?.revenue ?? null,
        nfce_count: rev?.nfce_count ?? null,
      };
    })
    .sort((a, b) => a.company_name.localeCompare(b.company_name));
});

const filteredCompanies = computed(() => {
  const term = searchTerm.value?.trim().toLowerCase();
  if (!term) return companies.value;
  const digits = term.replace(/\D/g, "");
  return companies.value.filter(
    (c) =>
      c.company_name?.toLowerCase().includes(term) ||
      (digits && c.company_cnpj?.replace(/\D/g, "").includes(digits)),
  );
});

const kpiCards = computed(() => {
  const t = tenant.value;
  return [
    {
      label: "Documentos",
      value: formatNumberLocal(t?.total_document_count ?? 0),
      color: config.colors.teal,
      icon: mdiFileDocumentMultiple,
    },
    {
      label: "Lojas",
      value: formatNumberLocal(t?.company_count ?? 0),
      color: "#818cf8",
      icon: mdiStore,
    },
    {
      label: "Dispositivos",
      value: formatNumberLocal(t?.active_device_count ?? 0),
      color: config.colors.primary,
      icon: mdiAccessPoint,
    },
    {
      label: "Usuários",
      value: formatNumberLocal(t?.user_count ?? 0),
      color: config.colors.danger,
      icon: mdiAccountGroup,
    },
  ];
});

const monthLabel = computed(() => {
  const month = format(currentDate.value, "MMMM", { locale: ptBR });
  const year = getYear(currentDate.value);
  return `${month} de ${year}`;
});

watch(currentDate, () => {
  const month = format(currentDate.value, "yyyy-MM");
  router.replace({ query: { month } }).catch(() => {});
});

// Revenue by company chart (horizontal bar)
const revenueByCompanyChartOption = computed(() => {
  const data = revenueByCompany.value?.data;
  if (!data?.length) return {};

  const sorted = [...data].sort((a, b) => a.revenue - b.revenue).slice(-10);

  return {
    color: SERIES_COLORS,
    tooltip: {
      trigger: "axis",
      appendToBody: true,
      axisPointer: { type: "shadow" },
      formatter(params) {
        const p = params[0];
        return `${p.name}<br/>${p.marker} Faturamento: <b>${formatMoney(p.value)}</b>`;
      },
    },
    grid: { left: 20, right: 20, top: 10, bottom: 10, containLabel: true },
    xAxis: {
      type: "value",
      splitLine: { lineStyle: { color: chartGrid.value } },
      axisLine: { show: false },
      axisLabel: {
        color: chartLabel.value,
        formatter(value) {
          if (value >= 1000000) return (value / 1000000).toFixed(1) + "M";
          if (value >= 1000) return (value / 1000).toFixed(0) + "k";
          return String(value);
        },
      },
    },
    yAxis: {
      type: "category",
      data: sorted.map((c) => c.company_name),
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { width: 150, overflow: "truncate", color: chartLabel.value },
    },
    series: [
      {
        type: "bar",
        colorBy: "data",
        barMaxWidth: 32,
        data: sorted.map((c) => c.revenue),
        itemStyle: {
          borderRadius: [0, 4, 4, 0],
        },
      },
    ],
  };
});

// Documents per company chart (horizontal bar)
const documentsChartOption = computed(() => {
  const companyList = companies.value;
  if (!companyList.length) return {};

  const sorted = [...companyList]
    .sort((a, b) => a.total_document_count - b.total_document_count)
    .slice(-10);

  return {
    color: SERIES_COLORS,
    tooltip: {
      trigger: "axis",
      appendToBody: true,
      axisPointer: { type: "shadow" },
      formatter(params) {
        const p = params[0];
        return `${p.name}<br/>${p.marker} Documentos: <b>${formatNumberLocal(p.value)}</b>`;
      },
    },
    grid: { left: 20, right: 20, top: 10, bottom: 10, containLabel: true },
    xAxis: {
      type: "value",
      splitLine: { lineStyle: { color: chartGrid.value } },
      axisLine: { show: false },
      axisLabel: {
        color: chartLabel.value,
        formatter(value) {
          if (value >= 1000) return (value / 1000).toFixed(0) + "k";
          return String(value);
        },
      },
    },
    yAxis: {
      type: "category",
      data: sorted.map((c) => c.company_name),
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { width: 150, overflow: "truncate", color: chartLabel.value },
    },
    series: [
      {
        type: "bar",
        colorBy: "data",
        barMaxWidth: 32,
        data: sorted.map((c) => c.total_document_count),
        itemStyle: {
          borderRadius: [0, 4, 4, 0],
        },
      },
    ],
  };
});

const barChartHeightPx = (itemCount) => {
  const perBar = 28;
  const padding = 30;
  return itemCount * perBar + padding;
};

const chartHeight = computed(() => {
  const revenueCount = Math.min(revenueByCompany.value?.data?.length || 0, 10);
  const docsCount = Math.min(companies.value.length, 10);
  const tallest = Math.max(revenueCount, docsCount);
  return barChartHeightPx(tallest) + "px";
});

const formatCnpj = (value) => {
  if (!value) return "";
  const cnpj = value.replace(/\D/g, "");
  if (cnpj.length !== 14) return value;
  return cnpj.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    "$1.$2.$3/$4-$5",
  );
};

const formatNumber = (value) => {
  if (value == null) return "";
  return value.toLocaleString("pt-BR");
};

const getExportFilename = (response, tId, fmt) => {
  const disposition =
    response.headers?.["content-disposition"] ||
    response.headers?.["Content-Disposition"];
  if (disposition) {
    const match = disposition.match(
      /filename\*=(?:UTF-8''|)([^;]+)|filename="?([^";]+)"?/i,
    );
    const filename = (match && (match[1] || match[2]))?.trim();
    if (filename) {
      try {
        return decodeURIComponent(filename);
      } catch {
        return filename;
      }
    }
  }
  const extension = fmt === "pdf" ? "pdf" : "xlsx";
  const month = format(currentDate.value, "yyyy-MM");
  return `faturamento-${tId}-${month}.${extension}`;
};

const exportMetrics = async (fmt) => {
  $snackbar.info("Baixando relatório...");
  const id = tenantId.value;
  const monthStart = startOfMonth(currentDate.value);
  const monthEnd = endOfMonth(currentDate.value);

  try {
    const response = await exportConsolidatedBillingMetrics(fmt, {
      date_start: format(monthStart, "yyyy-MM-dd"),
      date_end: format(monthEnd, "yyyy-MM-dd"),
      environment: "production",
      "tenant_ids[]": id ? [id] : undefined,
    });
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = getExportFilename(response, id, fmt);
    link.click();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    $snackbar.error(`Erro ao exportar: ${e.message}`);
  }
};
</script>

<style scoped>
.stat-icon {
  opacity: 0.18;
}
</style>
